import React, {useEffect, useRef, useState} from "react";
import {Button, Tabs, Space, message, Modal, Input as AntInput, Tooltip} from "antd";
import {PlusOutlined, SaveOutlined} from "@ant-design/icons";
import setTitle from "../helpers/setTitle";
import {Can} from "../helpers/Can";
import TaxonomyTable from "./TaxonomyTable";
import {Formik} from "formik";
import api from "../api";
import { handleApiError } from "../helpers/formatApiError";
import {FormItem, Input} from "formik-antd";
import FloatLabel from "../helpers/FloatLabel";
import Content from "../Content";
const { TabPane } = Tabs;
const { Search } = AntInput;

import {
    isBrowser,
    isMobile
} from "device-detect";
import {RefreshIcon} from "../helpers/icons";
import {useTranslation} from "react-i18next";
import TagImports, {NewTagImportButton} from "./TagImports";
import {NewTagTypeFields, newTagValidationSchema} from "../widgets/NewTagFormFields";
import ConfigurableEntityIcon from "../configurable_entities/ConfigurableEntityIcon";
import useCurrentOrg from "../helpers/useCurrentOrg";

export default () => {
    const {t} = useTranslation();
    const currentOrg = useCurrentOrg();
    const metadataEntitiesEnabled = currentOrg?.configurable_entities_enabled && currentOrg?.metadata_entities_enabled;
    setTitle(t('taxonomy','Taxonomy'));

    // --- Browse Tags state ---
    const [searchValue, setSearchValue] = useState()
    const [searchLoading, setSearchLoading] = useState()
    const [newTag, setNewTag] = useState()
    const [refresh, setRefresh] = useState(0)
    const [metadataEntityTypes, setMetadataEntityTypes] = useState([])

    useEffect(() => {
        if (!metadataEntitiesEnabled) {
            setMetadataEntityTypes([]);
            return;
        }
        api('/api/metadata_entity_types').then(res => setMetadataEntityTypes(res.data || []))
    }, [currentOrg?.id, metadataEntitiesEnabled])

    const onSearch = _.debounce((value) => {
        setSearchLoading(true);
        setSearchValue(value);
        switchTab('browse');
    }, 250)

    const onChange = (e) => {
        e.persist()
        onSearch(e.target.value)
    }

    const onLoaded = ()=> {
        setSearchLoading(false);
    }

    const onSave = tag =>{
       setNewTag(tag)
    }

    // --- Tag Imports state ---
    const [importSearchValue, setImportSearchValue] = useState()
    const [importSearchLoading, setImportSearchLoading] = useState()
    const [newTagImport, setNewTagImport] = useState()
    const [importRefresh, setImportRefresh] = useState(0)

    const onImportSearch = _.debounce((value) => {
        setImportSearchLoading(true);
        setImportSearchValue(value);
    }, 250)

    const onImportSearchChange = (e) => {
        e.persist()
        onImportSearch(e.target.value)
    }

    const onImportLoaded = () => {
        setImportSearchLoading(false);
    }

    const onImportCreate = (newTi) => {
        setNewTagImport(newTi)
    }

    const onImportDone = () => {
        setImportRefresh(r => r + 1)
    }

    // --- Tab + hash state ---
    const subTypeMap = {people: 'person', events: 'event', products: 'product', artwork: 'artwork'}
    const subTypeSlugs = Object.keys(subTypeMap)

    const parseHash = () => {
        const hash = window.location.hash.replace('#', '');
        if (['about', 'imports'].includes(hash)) return {tab: hash, subType: 'all'}
        const metadataEntityMatch = hash.match(/^browse-metadata-entity-(\d+)$/)
        if (metadataEntityMatch && metadataEntitiesEnabled) return {tab: 'browse', subType: `metadata_entity:${metadataEntityMatch[1]}`}
        for (const slug of subTypeSlugs) {
            if (hash === `browse-${slug}`) return {tab: 'browse', subType: subTypeMap[slug]}
        }
        if (hash === 'browse' || !hash) return {tab: 'browse', subType: 'all'}
        return {tab: 'browse', subType: 'all'}
    }

    const buildHash = (tab, subType) => {
        if (tab !== 'browse') return tab
        const metadataEntityId = metadataEntityTypeIdFromFilter(subType)
        if (metadataEntityId) return `browse-metadata-entity-${metadataEntityId}`
        const slug = Object.entries(subTypeMap).find(([, v]) => v === subType)?.[0]
        return slug ? `browse-${slug}` : 'browse'
    }

    const initial = parseHash()
    const [tab, setTab] = useState(initial.tab);
    const [subTypeFilter, setSubTypeFilter] = useState(initial.subType);
    const [tagImportFilter, setTagImportFilter] = useState(null);

    useEffect(() => {
        if(metadataEntitiesEnabled || !subTypeFilter?.startsWith('metadata_entity:')) return;

        setSubTypeFilter('all');
        window.location.hash = buildHash('browse', 'all');
    }, [metadataEntitiesEnabled, subTypeFilter]);

    const switchTab = (key) => {
        window.location.hash = buildHash(key, subTypeFilter);
        setTab(key);
    }

    const onSubTypeChange = (value) => {
        setSubTypeFilter(value);
        window.location.hash = buildHash('browse', value);
        setTab('browse');
    }

    useEffect(() => {
        const onHashChange = () => {
            const parsed = parseHash();
            setTab(parsed.tab);
            setSubTypeFilter(parsed.subType);
        };
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    const showImportTags = (tagImportId) => {
        setTagImportFilter(tagImportId);
        switchTab('browse');
    }

    // --- Contextual tab bar extras ---
    const BrowseExtra = (
        <Space>
            <Search
                placeholder={t('placeholder-search-tags','Search Tags...')}
                onSearch={onSearch}
                style={{width:'100%'}}
                size={'medium'}
                allowClear
                loading={searchLoading}
                onChange={onChange}
            />
            <Can I={'create'} a={'Tag'}>
                <NewTagButton onSave={onSave} subTypeFilter={subTypeFilter} metadataEntityTypes={metadataEntityTypes}/>
            </Can>
            <Tooltip title={t('tooltip.reload-tags','Reload Tags')}>
                <Button icon={<RefreshIcon/>} type='text' onClick={()=> setRefresh(refresh + 1)}/>
            </Tooltip>
        </Space>
    )

    const ImportsExtra = (
        <Space>
            <Search
                placeholder={t('placeholder-search-tag-imports','Search Tag Imports...')}
                onSearch={onImportSearch}
                style={{width:'100%'}}
                size={'medium'}
                allowClear
                loading={importSearchLoading}
                onChange={onImportSearchChange}
            />
            <Can I={'create'} a={'TagImport'}>
                <NewTagImportButton onCreate={onImportCreate} onImportDone={onImportDone}/>
            </Can>
            <Tooltip title={t('tooltip.reload-tag-imports','Reload Tag Imports')}>
                <Button icon={<RefreshIcon/>} type='text' onClick={()=> setImportRefresh(r => r + 1)}/>
            </Tooltip>
        </Space>
    )

    const Extra = tab === 'imports' ? ImportsExtra : BrowseExtra;

    return (
        <>
            {isMobile() && <div style={{margin:'.5em 0'}}>{Extra}</div>}

            <Tabs
                activeKey={tab}
                onTabClick={(key)=> switchTab(key)}
                tabBarExtraContent={isBrowser() && Extra}>
                <TabPane tab={t("about","About")} key="about">
                    <Content code={'about-taxonomy'}/>
                </TabPane>
                <TabPane tab={t("browse-tags","Browse Tags")} key="browse">
                    <TaxonomyTable q={searchValue} onLoaded={onLoaded} newTag={newTag} refresh={refresh} tagImportFilter={tagImportFilter} onClearImportFilter={() => setTagImportFilter(null)} subTypeFilter={subTypeFilter} onSubTypeChange={onSubTypeChange} metadataEntityTypes={metadataEntityTypes}/>
                </TabPane>
                <TabPane tab={t("tag-imports","Tag Imports")} key="imports">
                    <TagImports
                        onShowImportTags={showImportTags}
                        q={importSearchValue}
                        onLoaded={onImportLoaded}
                        newTagImport={newTagImport}
                        refresh={importRefresh}
                    />
                </TabPane>
            </Tabs>
        </>
    );
}

const NewTagButton = ({onSave, subTypeFilter, metadataEntityTypes = []}) => {
    const {t} = useTranslation();
    const [modalVisible, setModalVisible] = useState()
    const [inventoryStatus, setInventoryStatus] = useState(null)
    const onClick = ()=> {
        setModalVisible(true)
        setTimeout(()=> autoFocusInput.current?.focus(), 100)
    }

    const autoFocusInput = useRef(null);
    const selectedMetadataEntityTypeId = metadataEntityTypeIdFromFilter(subTypeFilter)
    const selectedMetadataEntityType = metadataEntityTypes.find(type => type.id === selectedMetadataEntityTypeId)
    const defaultSubType = selectedMetadataEntityTypeId ? 'metadata_entity' : (subTypeFilter && subTypeFilter !== 'all' ? subTypeFilter : 'keyword')
    const initialValues = {sub_type: defaultSubType}
    if(selectedMetadataEntityTypeId) initialValues.metadata_entity_type_id = selectedMetadataEntityTypeId

    return (<>
        <Button type='primary' onClick={onClick}>
            <PlusOutlined/>
            {t('button-add-new-tag','Add New Tag')}
        </Button>
        <Formik
            key={`${defaultSubType}-${selectedMetadataEntityTypeId || 'standard'}`}
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={(values, actions) => {
                const data = {tag: values}

                api.post(`/api/tags/`, data).then(res => {
                    actions.resetForm();
                    actions.setSubmitting(false);

                    setModalVisible(false);
                    message.success(t('message-tag-created','Tag created.'))

                    onSave && onSave(res.data)
                }).catch((err)=>{
                    handleApiError(err)
                })
            }}
            validationSchema={newTagValidationSchema}
        >
            {({values, submitForm, handleSubmit, isSubmitting, setFieldValue, isValid, dirty, resetForm}) => {
                const onKeyDownCapture = (e) => {
                    if(e.keyCode == 13) {
                        e.stopPropagation();
                        e.preventDefault();
                        submitForm();
                    }
                }

                const onCancel = () => {
                    resetForm();
                    setInventoryStatus(null);
                    setModalVisible(false);
                }

                const createDisabled = !dirty || !isValid || inventoryStatus === 'taken'

                return (
                    (<Modal
                        title={t('new-tag','New Tag')}
                        open={modalVisible}
                        destroyOnClose
                        onCancel={onCancel}
                        centered
                        confirmLoading
                        footer={
                            <Space direction={'horizontal'}>
                                <Button type={'primary'} onClick={submitForm} loading={isSubmitting} disabled={createDisabled}>
                                    <SaveOutlined/>
                                    {t('button-create','Create')}
                                </Button>
                                <Button onClick={onCancel}>{t('button-cancel','Cancel')}</Button>
                            </Space>
                        }
                    >
                        <form onSubmit={handleSubmit} onKeyDownCapture={onKeyDownCapture}>
                            <FormItem required name='name' showValidateSuccess>
                                <FloatLabel label={t('name','Name')} name={'name'} value={values?.name} description={t('placeholder-tag-name','e.g. Marketing')}>
                                    <Input size={'large'} required name='name' ref={autoFocusInput} autoFocus autoComplete='nope' data-1p-ignore/>
                                </FloatLabel>
                            </FormItem>
                            <FormItem name='description' style={{marginTop:'.5em'}}>
                                <FloatLabel label={t('description','Description')} name={'description'} value={values.description} description={t('optional','Optional.')}>
                                    <Input.TextArea rows={2} name='description' autoComplete='nope'/>
                                </FloatLabel>
                            </FormItem>

                            {selectedMetadataEntityType && (
                                <div style={{margin: '.75em 0'}}>
                                    <ConfigurableEntityIcon icon={selectedMetadataEntityType.icon} kind="metadata_entity"/> {selectedMetadataEntityType.name}
                                </div>
                            )}

                            <NewTagTypeFields
                                values={values}
                                setFieldValue={setFieldValue}
                                hideTypeSelect={!!selectedMetadataEntityType}
                                onInventoryStatus={setInventoryStatus}
                            />
                        </form>
                    </Modal>)
                );
            }}
        </Formik>
    </>);
    }

const metadataEntityTypeIdFromFilter = value => {
    const match = value?.toString().match(/^metadata_entity:(\d+)$/)
    return match ? parseInt(match[1], 10) : null
}
